import React, { useEffect, useState } from "react";
import { Card, List, Tag, Timeline, Button, Space, Spin, Alert, Divider, Modal, message } from "antd";
import { LeftOutlined, CheckOutlined, CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { axiosInstance, BASE_API_CONFIG } from "../../util/Api";
import { fetchStart, fetchSuccess } from "../../appRedux/actions";

const { confirm } = Modal;

const FreightBookingDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      confirmed: 'green',
      in_transit: 'blue',
      delivered: 'purple',
      cancelled: 'red',
    };
    return colors[status] || 'default';
  };

  const getBookingById = async (bookingId) => {
    if (!bookingId) {
      setError('Booking ID is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await axiosInstance.get(
        `${BASE_API_CONFIG.baseURL}/admin/freight-bookings/${bookingId}`
      );
      setBookingData(response?.data?.data);
    } catch (error) {
      console.error('Error fetching booking details:', error);
      setError('Failed to load booking details');
    } finally {
      setLoading(false);
      dispatch(fetchSuccess());
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchStart());
      getBookingById(id);
    } else {
      setError('No booking ID provided');
      setLoading(false);
    }
  }, [dispatch, id]);

  // Function to update booking status
  const updateBookingStatus = async (status, adminRemarks = '') => {
  setUpdating(true);
  try {
    const response = await axiosInstance.post( // Change from put to post
      `${BASE_API_CONFIG.baseURL}/admin/freight-bookings/${id}/status`,
      {
        status: status,
        adminRemarks: adminRemarks
      }
    );
    
    if (response.data.success) {
      message.success(`Booking ${status === 'confirmed' ? 'confirmed' : 'cancelled'} successfully`);
      // Refresh the booking data
      await getBookingById(id);
    } else {
      message.error('Failed to update booking status');
    }
  } catch (error) {
    console.error('Error updating booking status:', error);
    message.error('Failed to update booking status');
  } finally {
    setUpdating(false);
  }
};

  const handleConfirm = () => {
    confirm({
      title: 'Confirm Booking',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to confirm this freight booking?',
      okText: 'Yes, Confirm',
      okType: 'primary',
      cancelText: 'No',
      onOk() {
        updateBookingStatus('confirmed', 'Booking confirmed by admin');
      },
    });
  };

  const handleReject = () => {
    confirm({
      title: 'Reject Booking',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to reject this freight booking?',
      okText: 'Yes, Reject',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        // You can add a modal for rejection reason here
        Modal.confirm({
          title: 'Rejection Reason',
          content: (
            <div>
              <p>Please provide a reason for rejection:</p>
              <textarea 
                id="rejectionReason"
                placeholder="Enter rejection reason..."
                style={{ width: '100%', minHeight: '100px', marginTop: '10px', padding: '8px' }}
              />
            </div>
          ),
          onOk() {
            const reason = document.getElementById('rejectionReason').value;
            updateBookingStatus('cancelled', reason || 'Booking rejected by admin');
          }
        });
      },
    });
  };

  const timelineItems = [
    {
      color: 'green',
      children: 'Booking Created',
    },
    {
      color: bookingData?.status !== 'pending' ? 'green' : 'gray',
      children: 'Admin Confirmation',
    },
    {
      color: ['in_transit', 'delivered'].includes(bookingData?.status) ? 'green' : 'gray',
      children: 'In Transit',
    },
    {
      color: bookingData?.status === 'delivered' ? 'green' : 'gray',
      children: 'Delivered',
    },
  ];

  // Booking information data for the list
  const bookingInfoData = [
    {
      label: 'Booking ID',
      value: `${bookingData?.id}`,
    },
    {
      label: 'Booking Type',
      value: `${bookingData?.bookingType}`,
    },
    {
      label: 'Company Name',
      value: bookingData?.user?.companyName || 'N/A',
    },
    {
      label: 'Product Name',
      value: bookingData?.productName || 'N/A',
    },
    {
      label: 'Container Type',
      value: bookingData?.containerType || 'N/A',
    },
    {
      label: 'Number of Containers',
      value: bookingData?.numberOfContainers || 'N/A',
    },
    {
      label: 'Route',
      value: `${bookingData?.loadingPort || 'N/A'} → ${bookingData?.destinationPort || 'N/A'}`,
    },
    {
      label: 'Cargo Readiness Date',
      value: bookingData?.cargoReadinessDate 
        ? new Date(bookingData.cargoReadinessDate).toLocaleDateString()
        : 'N/A',
    },
    {
      label: 'Net Weight Per Container',
      value: bookingData?.netWeightPerContainer ? `${bookingData.netWeightPerContainer} kg` : 'N/A',
    },
    {
      label: 'Freight Rate',
      value: bookingData?.freightRate
        ? `${bookingData?.currency} ${parseFloat(bookingData.freightRate)}`
        : 'N/A',
    },
    {
      label: 'Special Requirements',
      value: bookingData?.specialRequirements || 'None',
    },
    ...(bookingData?.shippingCost ? [{
      label: 'Shipping Cost',
      value: `$${bookingData.shippingCost}`,
    }] : []),
    ...(bookingData?.insuranceCost ? [{
      label: 'Insurance Cost',
      value: `$${bookingData.insuranceCost}`,
    }] : []),
    ...(bookingData?.totalCost ? [{
      label: 'Total Cost',
      value: <strong>${bookingData.totalCost}</strong>,
    }] : []),
    {
      label: 'Status',
      value: (
        <Tag color={getStatusColor(bookingData?.status)}>
          {bookingData?.status ? bookingData.status.replace('_', ' ').toUpperCase() : 'PENDING'}
        </Tag>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => history.goBack()}>
              Go Back
            </Button>
          }
        />
      </Card>
    );
  }

  if (!bookingData) {
    return (
      <Card>
        <Alert
          message="Not Found"
          description="Booking not found"
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={() => history.goBack()}>
              Go Back
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <Card className="myexim-detail-card">
      <div className="myexim-table-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <LeftOutlined
          className="back-icon"
          style={{ cursor: 'pointer' }}
          onClick={() => history.goBack()}
        />
        Freight Booking Information
      </div>

      <Card className="gx-mt-4">
        <List
          dataSource={bookingInfoData}
          renderItem={(item) => (
            <List.Item>
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', padding: '8px 0' }}>
                <strong style={{ minWidth: '200px' }}>{item.label}:</strong>
                <span style={{ flex: 1, textAlign: 'left' }}>{item.value}</span>
              </div>
            </List.Item>
          )}
        />
      </Card>

      {bookingData.adminRemarks && (
        <Card title="Admin Remarks" className="gx-mt-4">
          <p>{bookingData.adminRemarks}</p>
        </Card>
      )}

      {bookingData.status === 'pending' && (
        <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 24 }}>
          <Button
            type="primary"
            size="large"
            icon={<CheckOutlined />}
            onClick={handleConfirm}
            loading={updating}
            disabled={updating}
          >
            CONFIRM BOOKING
          </Button>
          <Button
            type="primary"
            danger
            size="large"
            icon={<CloseOutlined />}
            onClick={handleReject}
            loading={updating}
            disabled={updating}
          >
            REJECT BOOKING
          </Button>
        </div>
      )}

      {/* Show status message for non-pending bookings */}
      {/* {bookingData.status !== 'pending' && (
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Alert
            message={`Booking is ${bookingData.status.toUpperCase()}`}
            type={bookingData.status === 'confirmed' ? 'success' : bookingData.status === 'cancelled' ? 'error' : 'info'}
            showIcon
          />
        </div>
      )} */}
    </Card>
  );
};

export default FreightBookingDetails;